import React, { useState, useEffect, useRef } from 'react';
import { isDesktop } from 'react-device-detect';
import DashboardDateTimePicker from './DashboardDateTimePicker';
import DashboardTitle from './DashboardTitle';
import { Row, Col, ButtonGroup, Button, IconButton } from '../../ui-kit';
import { IconCog, IconPlus, IconSave, IconTrash, IconRightFromBracket, IconEye, IconEdit } from '../../ui-kit/icons';
import DashboardModePicker from './DashboardModePicker';
import './DashboardToolbar.scss';

const DashboardToolbarEditable = ({
  exitEditModeWithConfirm,
  activateNormalMode,
  activateIncidentMode,
  incidentMode,
  incidentModeEnabled,
  deletePendingConfirm,
  deleteDashboardWithConfirm,
  editMode,
  dashboardTitle,
  accessibleDashboards,
  currentDashboard,
  groupDashboardEditRights,
  showNewDashboardModal,
  updateTitle,
  dashboardIsDefault,
  showDashboardSettings,
  showWidgetStore,
  unsavedChanges,
  saveDashboard
}) => (
  <Row className="g-2 pb-3 pt-3 dashboard-header-edit position-fixed d-print-none">
    <Col className="text-start">
      <Button variant="primary" className="me-2" onClick={exitEditModeWithConfirm}>
        <IconRightFromBracket /> {I18n.t('exit')}
      </Button>
      {incidentModeEnabled && (
        <DashboardModePicker
          activateNormalMode={activateNormalMode}
          activateIncidentMode={activateIncidentMode}
          incidentMode={incidentMode}
        />
      )}
      <Button variant={'success'} onClick={saveDashboard} className={'me-2'} disabled={!unsavedChanges}>
        <IconSave /> {I18n.t('save')}
      </Button>
      <Button variant={deletePendingConfirm ? 'danger' : 'outline-danger'} onClick={deleteDashboardWithConfirm}>
        <IconTrash /> {deletePendingConfirm ? I18n.t('confirm') : I18n.t('delete')}
      </Button>
    </Col>
    <Col className="text-center col-auto">
      <DashboardTitle
        editMode={editMode}
        dashboardTitle={dashboardTitle}
        accessibleDashboards={accessibleDashboards}
        currentDashboard={currentDashboard}
        groupDashboardEditRights={groupDashboardEditRights}
        showNewDashboardModal={showNewDashboardModal}
        updateTitle={updateTitle}
        dashboardIsDefault={dashboardIsDefault}
      />
    </Col>
    <Col className="text-end">
      <Button variant="primary" className="me-2" onClick={showDashboardSettings}>
        <IconCog /> {I18n.t('settings')}
      </Button>
      <Button variant="primary" onClick={showWidgetStore}>
        <IconPlus /> {I18n.t('dashboards.toolbar.add_widget')}
      </Button>
    </Col>
  </Row>
);

const DashboardToolbarReadOnly = ({
  setShowToolbar,
  enterEditMode,
  editMode,
  dashboardTitle,
  accessibleDashboards,
  currentDashboard,
  groupDashboardEditRights,
  editRights,
  showNewDashboardModal,
  updateTitle,
  setDashboardDatetime,
  dashboardDatetime,
  unlockDashboardDatetime
}) => (
  <Row className="g-2 dashboard-header-readonly">
    <Col className="text-start d-print-none">
      <ButtonGroup>
        <IconButton Icon={IconEye} onClick={() => setShowToolbar(false)} title={I18n.t('dashboards.toggle_toolbar')} />
        {isDesktop && editRights && <IconButton onClick={enterEditMode} Icon={IconEdit} title={I18n.t('edit')} />}
      </ButtonGroup>
    </Col>
    <Col className="col-auto text-center">
      <DashboardTitle
        editMode={editMode}
        dashboardTitle={dashboardTitle}
        accessibleDashboards={accessibleDashboards}
        currentDashboard={currentDashboard}
        groupDashboardEditRights={groupDashboardEditRights}
        showNewDashboardModal={showNewDashboardModal}
        updateTitle={updateTitle}
      />
    </Col>
    <Col className="text-end d-print-none">
      <DashboardDateTimePicker
        onDateChanged={setDashboardDatetime}
        datetime={dashboardDatetime}
        unlockDashboardDatetime={unlockDashboardDatetime}
      />
    </Col>
  </Row>
);

const DashboardToolbar = props => {
  const {
    incidentMode,
    editMode,
    enterEditMode,
    exitEditMode,
    activateNormalMode,
    activateIncidentMode,
    dashboardTitle,
    currentDashboard,
    dashboardIsDefault,
    incidentModeEnabled,
    updateTitle,
    accessibleDashboards,
    groupDashboardEditRights,
    editRights,
    showWidgetStore,
    showNewDashboardModal,
    saveDashboard,
    deleteDashboard,
    showDashboardSettings,
    unsavedChanges,
    updateDashboardFields
  } = props;
  const [deletePendingConfirm, setDeletePendingConfirm] = useState(false);
  const localToolbarChange = useRef(false);

  const usePrevious = value => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };

  const previousDashboard = usePrevious(currentDashboard);

  useEffect(() => {
    // Do not save anything if the currentDashboard changes
    // because of the initial load. This avoids saving unnecessarily
    if (!previousDashboard?.id) return;

    // Only save changes made by this user. Changes received from other
    // sessions (via dashboard updates) must not be saved back, otherwise
    // multiple open windows keep overwriting each other endlessly.
    if (!localToolbarChange.current) return;
    localToolbarChange.current = false;

    saveDashboard();
  }, [currentDashboard.show_toolbar]);

  const setShowToolbar = value => {
    if (!currentDashboard.id) return;

    localToolbarChange.current = true;

    updateDashboardFields({
      show_toolbar: value
    });
  };

  // If the value is not present in the currentDashboard JSON, we default to
  // showing it.
  const showToolbar = currentDashboard.show_toolbar === undefined ? true : currentDashboard.show_toolbar;

  if (!showToolbar)
    return (
      <Button
        variant="secondary"
        className="position-fixed start-0 m-3"
        style={{ opacity: '50%', top: '3.4rem', zIndex: '999' }}
        title={I18n.t('dashboards.toggle_toolbar')}
        onClick={() => setShowToolbar(true)}
      >
        <IconEye />
      </Button>
    );

  const deleteDashboardWithConfirm = () => {
    if (deletePendingConfirm) {
      deleteDashboard();
      setDeletePendingConfirm(false);
    } else {
      setDeletePendingConfirm(true);
    }
  };

  const exitEditModeWithConfirm = () => {
    setDeletePendingConfirm(false);
    exitEditMode(false);
  };

  if (editMode) {
    return (
      <DashboardToolbarEditable
        exitEditModeWithConfirm={exitEditModeWithConfirm}
        activateNormalMode={activateNormalMode}
        activateIncidentMode={activateIncidentMode}
        incidentMode={incidentMode}
        incidentModeEnabled={incidentModeEnabled}
        deletePendingConfirm={deletePendingConfirm}
        deleteDashboardWithConfirm={deleteDashboardWithConfirm}
        editMode={editMode}
        dashboardTitle={dashboardTitle}
        accessibleDashboards={accessibleDashboards}
        currentDashboard={currentDashboard}
        groupDashboardEditRights={groupDashboardEditRights}
        showNewDashboardModal={showNewDashboardModal}
        updateTitle={updateTitle}
        dashboardIsDefault={dashboardIsDefault}
        showDashboardSettings={showDashboardSettings}
        showWidgetStore={showWidgetStore}
        unsavedChanges={unsavedChanges}
        saveDashboard={saveDashboard}
      />
    );
  }

  return (
    <DashboardToolbarReadOnly
      setShowToolbar={setShowToolbar}
      enterEditMode={enterEditMode}
      editMode={editMode}
      dashboardTitle={dashboardTitle}
      accessibleDashboards={accessibleDashboards}
      currentDashboard={currentDashboard}
      groupDashboardEditRights={groupDashboardEditRights}
      editRights={editRights}
      showNewDashboardModal={showNewDashboardModal}
      updateTitle={updateTitle}
      setDashboardDatetime={props.setDashboardDatetime}
      dashboardDatetime={props.dashboardDatetime}
      unlockDashboardDatetime={props.unlockDashboardDatetime}
    />
  );
};

export default DashboardToolbar;
